import React from 'react';

import {
  Container,
  Button,
  Arrange,
  Spacer,
  Align,
  TextButton,
  useMedia,
} from '@loomhq/lens';
import CommentTextArea from '../comment-text-area/index';
import { usePlayerFromContext, useVideoContext } from '@loomhq/createhq';
import { useCommentsFullsize, useCreateComment } from '../hooks/comments';
import { secondsToVideoTS } from 'utilities/time';

import { ENTER, ESCAPE } from 'constants/keyCodes';
import {
  VIDEO_COMMENT_REPLY_CREATED,
  VIDEO_COMMENT_CREATED,
} from 'constants/events';

const IN_PLAYER_SIZE = 'small';

const InPlayerCommentInput = ({
  comment,
  placeholderText,
  setComment,
  onKeyDown,
  handleComment,
}) => {
  const player = usePlayerFromContext();

  const handleCancel = () => {
    player?.commentFormToggle();
  };

  const handleCommentAndExit = () => {
    handleComment();
    handleCancel(); // TODO: what if handleComment fails? should this be a cb on handleComment?
  };

  // TODO: VV: Currently we break on page size, hook into viewport context when that's available.
  const afterXs = useMedia(['(min-width: 800px)'], [true], false);

  const columns = afterXs ? ['1fr', 'auto'] : '1fr';
  const gap = afterXs ? 'small' : 'xsmall';

  const buttons = [
    <Button
      key={1}
      onClick={handleCommentAndExit}
      size={IN_PLAYER_SIZE}
      variant="primary"
    >
      {placeholderText}
    </Button>,
    <TextButton key={2} onClick={handleCancel} size={IN_PLAYER_SIZE}>
      Cancel
    </TextButton>,
  ];

  return (
    <Align alignment="center">
      <Arrange
        columns={columns}
        gap={gap}
        width="100%"
        maxWidth={120}
        alignItems="end"
      >
        <CommentTextArea
          comment={comment}
          placeholder={placeholderText}
          onChange={setComment}
          onKeyDown={onKeyDown}
          autofocus={true}
          inputSize="small"
        />
        <Arrange gap="small" justifyContent="end">
          {afterXs ? buttons : buttons.reverse()}
        </Arrange>
      </Arrange>
    </Align>
  );
};

const OverlayCommentInput = ({
  comment,
  placeholderText,
  setComment,
  onKeyDown,
  handleComment,
  closeReply,
  isReply,
}) => {
  const fullSize = useCommentsFullsize();
  const inputSize = fullSize ? 'medium' : 'small';
  const buttonText = isReply ? 'Reply' : placeholderText;
  const showButtons = isReply || comment;

  return (
    <Container paddingTop="large">
      <CommentTextArea
        comment={comment}
        placeholder={placeholderText}
        onChange={setComment}
        onKeyDown={onKeyDown}
        autofocus={isReply}
        inputSize={inputSize}
      />
      {showButtons ? (
        <Arrange justifyContent="end" gap="small">
          {isReply && (
            <TextButton size={inputSize} onClick={closeReply}>
              Cancel
            </TextButton>
          )}
          <Button onClick={handleComment} size={inputSize} variant="primary">
            {buttonText}
          </Button>
        </Arrange>
      ) : (
        // drawing a spacer when the button is not present allows for a nicer scroll-to-bottom experience
        <Spacer top="large" />
      )}
    </Container>
  );
};

const CommentInput = ({
  inPlayer,
  replyTo,
  closeReply,
}: {
  inPlayer?: boolean;
  replyTo?: string;
  closeReply?: () => void;
}): JSX.Element => {
  const player = usePlayerFromContext();
  const { video } = useVideoContext();
  const [comment, setComment] = React.useState('');

  const isReply = Boolean(replyTo);

  const placeholderText = isReply
    ? 'Leave a Reply'
    : `Comment at ${secondsToVideoTS(Math.round(player?.currentTime || 0))}`;

  const createComment = useCreateComment();

  const handleComment = () => {
    createComment({
      content: comment,
      parentPostId: replyTo ? parseInt(replyTo) : undefined,
    });

    setComment('');
    closeReply && closeReply();

    const eventType = isReply
      ? VIDEO_COMMENT_REPLY_CREATED
      : VIDEO_COMMENT_CREATED;

    analytics.track(eventType, {
      video_id: video.modelId,
      comment_length: comment.length,
      freshPlayer: true,
    });
  };

  const canSubmitComment = () => {
    return true;
  };

  const onKeyDown = e => {
    if (canSubmitComment() && e.keyCode === ENTER && !e.shiftKey) {
      e.preventDefault();
      handleComment();
    } else if (e.keyCode === ESCAPE) {
      closeReply && closeReply();
    }
  };

  if (inPlayer) {
    return (
      <InPlayerCommentInput
        comment={comment}
        placeholderText={placeholderText}
        setComment={setComment}
        onKeyDown={onKeyDown}
        handleComment={handleComment}
      />
    );
  }

  return (
    <OverlayCommentInput
      comment={comment}
      placeholderText={placeholderText}
      setComment={setComment}
      onKeyDown={onKeyDown}
      handleComment={handleComment}
      closeReply={closeReply}
      isReply={isReply}
    />
  );
};

export default CommentInput;
